import React from 'react';
import { Link } from 'react-router-dom';

import DocsLayout, {
  CodeBlock,
  Callout,
  pagerFor,
} from 'Containers/MCP/docs/DocsLayout';
import {
  MCP_ENDPOINT,
  TOOL_COUNT,
  RESOURCE_COUNT,
  TOOL_CATEGORIES,
} from 'data/mcpTools';

const PATH = '/mcp/docs/overview';
const { prev: PREV, next: NEXT } = pagerFor(PATH);

const TOC = [
  { href: '#what-it-is', label: 'What it is' },
  { href: '#what-you-can-do', label: 'What you can do' },
  { href: '#what-it-is-not', label: 'What it is not' },
  { href: '#what-you-need', label: 'What you need' },
  { href: '#remote-vs-local', label: 'Remote vs local' },
];

const Overview = () => (
  <DocsLayout
    eyebrow="Vocable MCP connector"
    title="Overview"
    lede={
      <>
        The Vocable MCP connector lets Claude work inside your Vocable account
        from an ordinary chat. Once it is connected, you can ask Claude to look
        through your content library, write in your brand voice, plan a campaign,
        or publish a document &mdash; and it does that work in Vocable itself,
        not in a copy of it.
      </>
    }
    stats={[
      { value: `${TOOL_COUNT}`, label: 'tools' },
      { value: `${TOOL_CATEGORIES.length}`, label: 'categories' },
      { value: `${RESOURCE_COUNT}`, label: 'brand resources' },
    ]}
    toc={TOC}
    prev={PREV}
    next={NEXT}
  >
    <h2 id="what-it-is">What it is</h2>
    <p>
      MCP (Model Context Protocol) is the standard Claude uses to talk to outside
      systems. Vocable runs a <strong>remote</strong> MCP server: a single hosted
      endpoint that Claude connects to over the internet.
    </p>
    <CodeBlock code={MCP_ENDPOINT} label="Connector URL" language="text" />
    <p>
      You authorise it once with the Vocable account you already have. There is{' '}
      <strong>no API key to generate</strong> and{' '}
      <strong>no local server to install or keep running</strong> &mdash; the sign-in
      happens in your browser, and Claude holds a short-lived access token
      afterwards.
    </p>
    <p>
      The connector exposes {TOOL_COUNT} tools across{' '}
      {TOOL_CATEGORIES.length} categories, plus {RESOURCE_COUNT} read-only brand
      resources that Claude can pull in as background context.
    </p>

    <h2 id="what-you-can-do">What you can do with it</h2>
    <ul className="mcp-docs__cards">
      {TOOL_CATEGORIES.map((category) => (
        <li className="mcp-docs__card" key={category.id}>
          <h3>{category.label}</h3>
          <p>{category.blurb}</p>
          <ul className="mcp-docs__tags">
            {category.tools.slice(0, 4).map((tool) => (
              <li className="mcp-docs__tag" key={tool.name}>
                {tool.name}
              </li>
            ))}
            {category.tools.length > 4 && (
              <li className="mcp-docs__tag">
                +{category.tools.length - 4} more
              </li>
            )}
          </ul>
        </li>
      ))}
    </ul>
    <p className="mcp-docs__after-cards">
      Every tool, with its full parameter list, is documented in the{' '}
      <Link to="/mcp/docs/tools">tool reference</Link>.
    </p>

    <h2 id="what-it-is-not">What it is not</h2>
    <ul>
      <li>
        <strong>It is not a replacement for the Vocable API.</strong> The
        connector is built for conversation with an AI assistant, not for
        server-to-server automation. If you are writing software against Vocable,
        use the API.
      </li>
      <li>
        <strong>The agent acts as you.</strong> Claude sees and can change
        exactly what your own account can see and change &mdash; same brands,
        same documents, same team permissions, same plan entitlements. Nothing is
        elevated, and nothing outside your account becomes visible. The connector
        also applies its own per-tool rate limits on top of your account&rsquo;s;
        the{' '}
        <code className="mcp-docs__code-inline">rate_limit_status</code> tool
        reports where you stand if the agent is ever told to slow down.
      </li>
      <li>
        <strong>It is not unsupervised.</strong> Tools that create, update,
        publish or share something are written to ask you to confirm first, and
        Claude will normally show you what it is about to do.
      </li>
    </ul>
    <p>
      How the connection is authorised, and what the token can reach, is covered
      in <Link to="/mcp/docs/security">authentication &amp; security</Link>.
    </p>

    <h2 id="what-you-need">What you need</h2>
    <ul>
      <li>A Vocable account.</li>
      <li>
        At least one brand set up in that account. Brands are what everything
        else is scoped to, so the tools need one before they can do much.
      </li>
      <li>
        <strong>Claude on the web</strong> (claude.ai), where custom connectors
        are added. Claude Desktop then picks the same connector up automatically
        &mdash; see <Link to="/mcp/docs/clients">supported clients</Link>.
      </li>
    </ul>

    <h2 id="how-it-works">How it works</h2>
    <p>
      The Vocable MCP connector is hosted by Vocable &mdash; there is nothing to
      install. You paste the connector URL into your AI client, sign in with
      your Vocable account via OAuth, and you are connected. No API key needed.
    </p>

    <h2>Start here</h2>
    <ul className="mcp-docs__linkcards">
      <li>
        <Link to="/mcp/docs/quickstart">
          <strong>Connect the connector</strong>
          <span>
            Paste one URL, sign in, done &mdash; about two minutes, no API key.
          </span>
        </Link>
      </li>
      <li>
        <Link to="/mcp/docs/first-session">
          <strong>Your first session</strong>
          <span>
            Choose the brand the tools act on, then prompts worth trying first.
          </span>
        </Link>
      </li>
      <li>
        <Link to="/mcp/docs/tools">
          <strong>Tool reference</strong>
          <span>
            All {TOOL_COUNT} tools, generated from the live server.
          </span>
        </Link>
      </li>
    </ul>

    <Callout tone="tip" title="Two minutes, then you are in">
      <p>
        Connecting is genuinely just a URL and a sign-in. Everything else in
        these docs is about what to do afterwards.
      </p>
    </Callout>
  </DocsLayout>
);

export default Overview;
